// Iter 126 — Earnings Tracker, Uber-styled redesign.
// Iter 128 — Added currency picker + Share-my-month PNG export.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import TopNav from "../components/TopNav";
import CurrencyPicker from "../components/CurrencyPicker";
import ShareMonthModal from "../components/ShareMonthModal";
import { useAuth } from "../context/AuthContext";
import { fmtAmount, getCurrency } from "../lib/currency";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, TrendingUp, Flame, Trophy, Sparkles,
  Trash2, Plus, ArrowUpRight, ArrowDownRight, Wallet, Share2,
} from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const todayUtc = () => new Date().toISOString().slice(0, 10);
const dayKey = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const EarningsPage = () => {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth());
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDay, setEditDay] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [currency, setCurrency] = useState(() => (user?.currency || "USD").toUpperCase());

  // Sync currency when user object loads/changes (refresh after PATCH).
  useEffect(() => {
    if (user?.currency) setCurrency(user.currency.toUpperCase());
  }, [user?.currency]);

  const fmt = (v, opts) => fmtAmount(v, currency, opts);
  const cur = getCurrency(currency);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        axios.get(`${API}/me/earnings/monthly`, { params: { year, month: month + 1 } }),
        axios.get(`${API}/me/earnings/summary`),
      ]);
      setData(m.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load earnings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [year, month]);

  const entriesByDate = useMemo(() => {
    const map = {};
    (data?.entries || []).forEach((e) => { map[e.date] = e; });
    return map;
  }, [data]);

  const prevMonth = () => month === 0 ? (setMonth(11), setYear(year - 1)) : setMonth(month - 1);
  const nextMonth = () => month === 11 ? (setMonth(0), setYear(year + 1)) : setMonth(month + 1);

  const openCell = (date) => {
    const e = entriesByDate[date];
    setEditDay({
      date, stake: e?.stake ?? "", return_amount: e?.return_amount ?? "",
      note: e?.note ?? "", isExisting: !!e,
    });
  };

  const saveEntry = async () => {
    if (!editDay) return;
    const stake = parseFloat(editDay.stake);
    const ret = parseFloat(editDay.return_amount);
    if (isNaN(stake) || stake < 0) return toast.error("Stake must be a non-negative number");
    if (isNaN(ret) || ret < 0) return toast.error("Return must be a non-negative number");
    try {
      await axios.post(`${API}/me/earnings`, {
        date: editDay.date, stake, return_amount: ret,
        note: editDay.note || "", currency,
      });
      toast.success("Saved");
      setEditDay(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteEntry = async () => {
    if (!editDay?.date) return;
    try {
      await axios.delete(`${API}/me/earnings/${editDay.date}`);
      toast.success("Deleted");
      setEditDay(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const grid = useMemo(() => {
    if (!data) return [];
    const cells = [];
    for (let i = 0; i < (data.first_weekday || 0); i++) cells.push(null);
    for (let d = 1; d <= data.days_in_month; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [data]);

  const s = data?.summary;
  const allTime = summary || {};
  const pnl = s?.pnl ?? 0;
  const roi = s?.roi_pct ?? 0;
  const isUp = pnl >= 0;

  return (
    <div data-theme="dark" className="dark min-h-screen bg-gradient-to-b from-[#11151c] via-[#0c1014] to-[#0a0d11] text-foreground">
      <TopNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8" data-testid="earnings-page">

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
              <Wallet className="w-3.5 h-3.5" /> Earnings Tracker
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-[42px] leading-[1.05] tracking-tight mt-3">
              {MONTHS[month]} <span className="text-muted-foreground/60 font-medium">{year}</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              Tap any day to log your stake and return. We surface your
              monthly P/L, ROI, winning streaks and personal records.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <CurrencyPicker
                value={currency}
                onChange={setCurrency}
                data-testid="earnings-currency-picker"
              />
              <Button
                variant="outline"
                onClick={() => setShareOpen(true)}
                data-testid="earnings-share-btn"
                disabled={!data || (data?.summary?.days_tracked ?? 0) === 0}
                className="rounded-full h-9 px-4 text-xs font-bold uppercase tracking-[0.14em] bg-white/[0.04] border-white/10 hover:bg-white/[0.08]"
              >
                <Share2 className="w-3.5 h-3.5 mr-1.5 text-emerald-400" />
                Share my month
              </Button>
            </div>
          </div>

          {/* Compact stat duo — replaces the old solid-black bottom bar */}
          <div className="flex items-stretch gap-3">
            <div className={`rounded-2xl px-5 py-4 border min-w-[140px] ${
              isUp
                ? "border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent"
                : "border-rose-400/30 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent"
            }`} data-testid="earnings-month-pnl">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1">
                {isUp ? <ArrowUpRight className="w-3 h-3 text-emerald-400" /> : <ArrowDownRight className="w-3 h-3 text-rose-400" />}
                Net P/L · {cur.code}
              </div>
              <div className={`font-display font-bold text-3xl sm:text-4xl tracking-tight mt-1 ${isUp ? "text-emerald-400" : "text-rose-400"}`}>
                {fmt(pnl)}
              </div>
            </div>
            <div className={`rounded-2xl px-5 py-4 border min-w-[120px] ${
              roi >= 0
                ? "border-emerald-400/30 bg-emerald-500/[0.04]"
                : "border-rose-400/30 bg-rose-500/[0.04]"
            }`} data-testid="earnings-month-roi">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">ROI</div>
              <div className={`font-display font-bold text-3xl sm:text-4xl tracking-tight mt-1 ${roi >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {roi.toFixed(0)}%
              </div>
            </div>
          </div>
        </header>

        {/* ── Quick stats strip ────────────────────────────────────── */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={<Flame className="w-3.5 h-3.5" />} label="Current streak" value={`${allTime?.current_streak ?? 0}`} suffix="days" tone="emerald" testid="ribbon-current-streak" />
          <Stat icon={<Trophy className="w-3.5 h-3.5" />} label="Best streak" value={`${allTime?.best_streak ?? 0}`} suffix="days" tone="amber" testid="ribbon-best-streak" />
          <Stat icon={<TrendingUp className="w-3.5 h-3.5" />} label="All-time P/L" value={fmt(allTime?.pnl ?? 0)} suffix="" tone={(allTime?.pnl ?? 0) >= 0 ? "emerald" : "rose"} testid="ribbon-alltime-pnl" />
          <Stat icon={<Sparkles className="w-3.5 h-3.5" />} label="Win rate" value={`${allTime?.win_rate_pct ?? 0}%`} suffix={`${allTime?.win_days ?? 0}/${allTime?.days_tracked ?? 0}`} tone="indigo" testid="ribbon-win-rate" />
        </section>

        {/* ── Calendar card ────────────────────────────────────────── */}
        <section
          className="rounded-3xl border border-white/[0.06] bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 sm:p-8 backdrop-blur"
          data-testid="earnings-calendar-card"
        >
          {/* Month switcher */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" onClick={prevMonth}
              data-testid="earnings-prev-month"
              className="rounded-full h-10 w-10 p-0 bg-white/5 hover:bg-white/10 border border-white/5">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-bold">
                {s?.days_tracked ?? 0} days · {s?.win_days ?? 0}W · {s?.loss_days ?? 0}L
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={nextMonth}
              data-testid="earnings-next-month"
              className="rounded-full h-10 w-10 p-0 bg-white/5 hover:bg-white/10 border border-white/5">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Day-of-week labels */}
          <div className="grid grid-cols-7 gap-2 mb-3">
            {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center font-bold">{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-7 gap-2">
            {loading
              ? Array.from({ length: 35 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-2xl bg-white/[0.04] animate-pulse" />
                ))
              : grid.map((dayNum, i) => {
                  if (!dayNum) return <div key={i} className="aspect-square" />;
                  const date = dayKey(year, month, dayNum);
                  return (
                    <DayCell
                      key={i}
                      day={dayNum}
                      entry={entriesByDate[date]}
                      isToday={date === todayUtc()}
                      currency={currency}
                      onClick={() => openCell(date)}
                    />
                  );
                })}
          </div>
        </section>

        {/* ── Personal records ─────────────────────────────────────── */}
        {allTime?.records && (allTime.records.best_day || allTime.records.worst_day) && (
          <section className="grid sm:grid-cols-3 gap-3" data-testid="earnings-records">
            <Record kind="best" entry={allTime.records.best_day} currency={currency} />
            <Record kind="worst" entry={allTime.records.worst_day} currency={currency} />
            <Record kind="comeback" entry={allTime.records.biggest_comeback} currency={currency} />
          </section>
        )}

        {/* ── Bankroll sparkline ───────────────────────────────────── */}
        {allTime?.cumulative?.length > 1 && (
          <BankrollChart points={allTime.cumulative} currency={currency} />
        )}

        <p className="text-center text-[10px] text-muted-foreground uppercase tracking-[0.2em] pt-4 pb-8">
          Bet responsibly · 18+ · Track to learn, not to chase
        </p>
      </main>

      {/* ── Share-my-month modal ─────────────────────────────────── */}
      <ShareMonthModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        monthlyData={data}
        summary={summary}
        year={year}
        month={month}
        userId={user?.id}
        currency={currency}
      />

      {/* ── Edit modal ──────────────────────────────────────────── */}
      <Dialog open={!!editDay} onOpenChange={(o) => !o && setEditDay(null)}>
        <DialogContent className="sm:max-w-md bg-[#12161d] border-white/[0.08]" data-testid="earnings-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editDay?.isExisting ? "Edit" : "Log"} · <span className="font-tabular text-muted-foreground font-medium">{editDay?.date}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="stake" className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Stake</Label>
              <Input
                id="stake" type="number" min="0" step="0.01"
                value={editDay?.stake ?? ""}
                onChange={(e) => setEditDay({ ...editDay, stake: e.target.value })}
                placeholder="100"
                data-testid="earnings-stake-input"
                className="mt-1.5 h-11 bg-white/[0.04] border-white/10 font-tabular text-lg"
              />
            </div>
            <div>
              <Label htmlFor="return_amount" className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Return (0 if lost)</Label>
              <Input
                id="return_amount" type="number" min="0" step="0.01"
                value={editDay?.return_amount ?? ""}
                onChange={(e) => setEditDay({ ...editDay, return_amount: e.target.value })}
                placeholder="287"
                data-testid="earnings-return-input"
                className="mt-1.5 h-11 bg-white/[0.04] border-white/10 font-tabular text-lg"
              />
            </div>
            <div>
              <Label htmlFor="note" className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Note (optional)</Label>
              <Input
                id="note" type="text" maxLength={140}
                value={editDay?.note ?? ""}
                onChange={(e) => setEditDay({ ...editDay, note: e.target.value })}
                placeholder="EPL acca · 5 legs"
                data-testid="earnings-note-input"
                className="mt-1.5 h-11 bg-white/[0.04] border-white/10"
              />
            </div>
            {editDay && (
              <div className="rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Net P/L</span>
                <span className={`font-display font-bold text-2xl tracking-tight ${
                  (parseFloat(editDay.return_amount) - parseFloat(editDay.stake) || 0) >= 0 ? "text-emerald-400" : "text-rose-400"
                }`}>
                  {fmt((parseFloat(editDay.return_amount) || 0) - (parseFloat(editDay.stake) || 0))}
                </span>
              </div>
            )}
          </div>
          <DialogFooter className="flex sm:justify-between gap-2 pt-3">
            {editDay?.isExisting && (
              <Button variant="ghost" onClick={deleteEntry} data-testid="earnings-delete-btn"
                className="text-rose-400 hover:bg-rose-500/10 h-10">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="ghost" onClick={() => setEditDay(null)} className="h-10">Cancel</Button>
              <Button onClick={saveEntry} data-testid="earnings-save-btn"
                className="bg-emerald-400 hover:bg-emerald-300 text-emerald-950 font-bold h-10 px-6 rounded-full">
                Save entry
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── DayCell ───
const DayCell = ({ day, entry, isToday, currency, onClick }) => {
  const has = !!entry && typeof entry.pnl === "number";
  const up = has && entry.pnl > 0;
  const down = has && entry.pnl < 0;
  const cls = up
    ? "border-emerald-400/40 bg-gradient-to-br from-emerald-500/[0.14] to-emerald-500/[0.05] text-emerald-300 hover:from-emerald-500/20"
    : down
    ? "border-rose-400/40 bg-gradient-to-br from-rose-500/[0.12] to-rose-500/[0.04] text-rose-300 hover:from-rose-500/20"
    : "border-white/[0.06] bg-white/[0.015] text-muted-foreground/50 hover:border-white/15 hover:bg-white/[0.04]";
  const cur = getCurrency(currency);
  const cellLabel = has
    ? (cur.prefix
        ? `${entry.pnl > 0 ? "+" : entry.pnl < 0 ? "-" : ""}${cur.symbol}${Math.abs(entry.pnl).toFixed(0)}`
        : `${entry.pnl > 0 ? "+" : entry.pnl < 0 ? "-" : ""}${Math.abs(entry.pnl).toFixed(0)} ${cur.symbol}`)
    : "";
  return (
    <button
      onClick={onClick}
      data-testid={`day-cell-${entry?.date || day}`}
      className={`group aspect-square rounded-2xl border p-2 sm:p-2.5 flex flex-col items-start justify-between transition-all duration-150 ${cls}`}
    >
      <span className={`text-xs sm:text-sm font-bold ${isToday ? "ring-2 ring-emerald-400/60 ring-offset-2 ring-offset-transparent rounded-full px-1.5" : ""}`}>
        {day}
      </span>
      {has ? (
        <span className="self-end font-tabular font-bold text-xs sm:text-base leading-none tracking-tight">
          {cellLabel}
        </span>
      ) : (
        <Plus className="self-end w-3.5 h-3.5 opacity-0 group-hover:opacity-40 transition" />
      )}
    </button>
  );
};

// ─── Stat (compact ribbon) ───
const Stat = ({ icon, label, value, suffix, tone, testid }) => {
  const tones = {
    emerald: "border-emerald-400/25 bg-emerald-500/[0.04] text-emerald-300",
    amber: "border-amber-400/25 bg-amber-500/[0.04] text-amber-300",
    rose: "border-rose-400/25 bg-rose-500/[0.04] text-rose-300",
    indigo: "border-indigo-400/25 bg-indigo-500/[0.04] text-indigo-300",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3.5 ${tones[tone] || tones.emerald}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold opacity-90">
        {icon} {label}
      </div>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <div className="font-display font-bold text-2xl sm:text-[28px] tracking-tight text-foreground">{value}</div>
        {suffix && <div className="text-[10px] uppercase tracking-wider opacity-70 font-medium">{suffix}</div>}
      </div>
    </div>
  );
};

// ─── Record chip ───
const Record = ({ kind, entry, currency }) => {
  const META = {
    best:     { label: "Best day ever",       icon: <Trophy className="w-3.5 h-3.5 text-amber-300" />, ring: "border-amber-400/25" },
    worst:    { label: "Worst day ever",      icon: <ArrowDownRight className="w-3.5 h-3.5 text-rose-300" />, ring: "border-rose-400/25" },
    comeback: { label: "Biggest comeback",    icon: <Sparkles className="w-3.5 h-3.5 text-emerald-300" />, ring: "border-emerald-400/25" },
  };
  const m = META[kind];
  if (!entry || !entry.date) {
    return (
      <div className={`rounded-2xl border ${m.ring} bg-white/[0.02] p-4`}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{m.icon} {m.label}</div>
        <div className="text-sm text-muted-foreground/60 mt-2">No data yet</div>
      </div>
    );
  }
  const v = entry.pnl;
  return (
    <div className={`rounded-2xl border ${m.ring} bg-white/[0.02] p-4`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{m.icon} {m.label}</div>
      <div className={`mt-1.5 font-display font-bold text-2xl tracking-tight ${v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : ""}`}>
        {fmtAmount(v, currency)}
      </div>
      <div className="text-xs text-muted-foreground font-tabular mt-0.5">{entry.date}</div>
      {kind === "comeback" && (
        <div className="text-[10px] text-muted-foreground/60 mt-1.5 italic">Largest win the day after a loss</div>
      )}
    </div>
  );
};

// ─── BankrollChart ───
const BankrollChart = ({ points, currency }) => {
  if (!points || points.length < 2) return null;
  const W = 800, H = 160, PAD = 24;
  const bals = points.map((p) => p.balance);
  const min = Math.min(...bals, 0);
  const max = Math.max(...bals, 0);
  const range = max - min || 1;
  const stepX = (W - PAD * 2) / (points.length - 1);
  const y = (v) => H - PAD - ((v - min) / range) * (H - PAD * 2);
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${PAD + i * stepX} ${y(p.balance)}`).join(" ");
  const last = points[points.length - 1];
  const up = last.balance >= 0;
  return (
    <section className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5 sm:p-6" data-testid="earnings-bankroll-chart">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Bankroll</div>
          <div className="font-display font-bold text-lg sm:text-xl tracking-tight mt-1">All-time P/L curve</div>
        </div>
        <div className="text-right">
          <div className={`font-display font-bold text-2xl tracking-tight ${up ? "text-emerald-400" : "text-rose-400"}`}>
            {fmtAmount(last.balance, currency)}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Latest</div>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-36" preserveAspectRatio="none">
        <defs>
          <linearGradient id="brfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={up ? "#34d399" : "#f87171"} stopOpacity="0.25" />
            <stop offset="100%" stopColor={up ? "#34d399" : "#f87171"} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1={PAD} y1={y(0)} x2={W - PAD} y2={y(0)} stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
        <path d={`${linePath} L ${PAD + (points.length - 1) * stepX} ${y(0)} L ${PAD} ${y(0)} Z`} fill="url(#brfill)" />
        <path d={linePath} stroke={up ? "#34d399" : "#f87171"} strokeWidth="2.5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </section>
  );
};

export default EarningsPage;
